// PilulierPage.js - Page Pilulier Intelligent (Coming Soon)
import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import {
  Bell,
  Pill,
  Clock,
  Calendar,
  Smartphone,
  CheckCircle,
  Plus,
  AlertCircle,
  Sparkles,
  Users,
  Shield,
} from 'lucide-react';

const PilulierPage = () => {
  const features = [
    {
      icon: Plus,
      title: 'Ajoutez vos médicaments',
      description:
        'Enregistrez facilement vos traitements en cours avec leur posologie et fréquence de prise.',
      color: '#3B82F6',
    },
    {
      icon: Clock,
      title: 'Définissez vos horaires',
      description:
        'Configurez les heures de prise pour chaque médicament selon votre prescription.',
      color: '#8B5CF6',
    },
    {
      icon: Smartphone,
      title: 'Rappels par SMS',
      description: 'Recevez un SMS automatique à chaque heure de prise pour ne jamais oublier.',
      color: '#10B981',
    },
    {
      icon: CheckCircle,
      title: "Suivi d'observance",
      description: 'Confirmez vos prises et visualisez votre historique de traitement.',
      color: '#F59E0B',
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Sécurité renforcée',
      description: 'Ne manquez plus jamais une prise importante',
    },
    {
      icon: Users,
      title: 'Pour toute la famille',
      description: 'Gérez les traitements de vos proches',
    },
    {
      icon: Calendar,
      title: 'Vue calendrier',
      description: 'Visualisez votre semaine de traitement',
    },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl shadow-xl mb-6">
            <Bell className="w-10 h-10 text-white" />
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Bientôt disponible
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Pilulier Intelligent
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Ne manquez plus jamais une prise de médicament grâce à nos rappels SMS automatiques.
            Gérez vos traitements en toute simplicité.
          </p>
        </div>

        {/* Comment ça marche */}
        <Card className="p-8 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-8 flex items-center gap-3">
            <Pill className="w-6 h-6 text-purple-500" />
            Comment ça fonctionne ?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex gap-4 p-5 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Avantages */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {benefits.map((benefit, index) => (
            <Card
              key={index}
              className="p-6 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
                <benefit.icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {benefit.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.description}</p>
            </Card>
          ))}
        </div>

        {/* Aperçu mockup */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800 mb-8">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-purple-600" />
            Exemple de rappel SMS
          </h3>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 max-w-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <span className="font-medium text-gray-900 dark:text-gray-100">Remadia</span>
              <span className="text-xs text-gray-500 ml-auto">08:00</span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              ⏰ Rappel : Il est l'heure de prendre votre <strong>Doliprane 1000mg</strong>. Bonne
              journée ! 💊
            </p>
          </div>
        </Card>

        {/* Info développement */}
        <div className="flex gap-4 p-5 rounded-2xl bg-gray-800 border-l-4 border-l-purple-500">
          <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-purple-400" />
          <div>
            <p className="text-sm md:text-base leading-relaxed text-gray-300">
              <strong className="text-white">Fonctionnalité en développement :</strong> Le Pilulier
              Intelligent sera disponible prochainement. Vous pourrez gérer vos traitements et
              recevoir des rappels SMS pour ne jamais oublier vos prises.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              📧 Une question ? Contactez-nous à{' '}
              <span className="text-purple-400">contact@remadia.fr</span>
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PilulierPage;
